import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { listUsers, getUserDetails, clearUserDetails, createNewUser, updateExistingUser } from "../../actions/userActions";
import UserModal from "./UserModal";

function UserManagement() {

    const dispatch = useDispatch();

    const [open, setOpen] = useState(false);
    const [modalMode, setModalMode] = useState("create");
    const [searchTerm, setSearchTerm] = useState("");

    const userList = useSelector(
        state => state.userList
    );

    const { users, loading } = userList;

    const userDetails = useSelector(
        state => state.userDetails
    );

    const { user } = userDetails;

    useEffect(() => {
        dispatch(listUsers());
    }, [dispatch]);

    const handleRowClick = async (serviceNumber) => {
        setModalMode("view");

        await dispatch(
            getUserDetails(serviceNumber)
        );

        setOpen(true);
    };

    const handleCreate = async (formData) => {
        await dispatch(
            createNewUser(formData)
        );

        dispatch(listUsers());

        setOpen(false);
    };

    const handleUpdate = async (formData) => {
        await dispatch(
            updateExistingUser(formData)
        );

        await dispatch(listUsers());

        setOpen(false);

        dispatch(clearUserDetails());
    }

    const filteredUsers = users?.filter((user) => {
        const search = searchTerm.toLowerCase().trim();

        return (
            user.ServiceNumber?.toLowerCase().includes(search) || 
            user.Name?.toLowerCase().includes(search)
        )
    }) || [];

    const isMobile = window.innerWidth < 768;

    return (
        <>
            <div className="w-full px-4 py-8 md:p-10">

                <div className="mb-8 flex flex-col md:flex-row md:justify-between md:items-end gap-4">

                    <div>
                        <p className="text-sm uppercase tracking-[4px] text-electricBlue font-semibold">
                            Administration
                        </p>

                        <h1 className="mt-2 text-3xl md:text-4xl font-bold text-textPrimary">
                            User Management
                        </h1>

                        <p className="text-sm md:text-base mt-2 text-textSecondary">
                            Manage user accounts, permissions and system access.
                        </p>
                    </div>

                    <button
                        onClick={() => {
                            dispatch(
                                clearUserDetails()
                            );
                            setModalMode("create");
                            setOpen(true);
                        }}
                        className="w-full md:w-auto px-5 py-3 bg-deepNavy text-white rounded-xl hover:bg-navy transition"
                    >
                        Add User
                    </button>

                </div>

                {/* Stat Cards */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-6 mb-8">
                    <StatCard 
                        label="Total Users"
                        value={users?.length || 0}
                    />

                    <StatCard 
                        label="Active Users"
                        value={
                            users?.filter(u => u.Status === "A").length
                        }
                    />

                    <StatCard 
                        label="Administrators"
                        value={
                            users?.filter(u => u.Role === "A").length
                        }
                    />
                </div>

                {/* Search */}
                <div className="flex gap-4 mb-6">
                    <input 
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search service number or name..."
                        className="flex-1 px-4 py-3 rounded-xl border border-border bg-white focus:border-electricBlue outline-none"
                    />
                </div>

                <div className="bg-white rounded-3xl border border-border overflow-x-auto max-w-full">
                    <table className="w-full min-w-[700px]">

                        <thead className="bg-surfaceSoft">

                            <tr>

                                <th className="px-6 py-4 text-center lg:text-left text-xs uppercase tracking-[2px] text-textSecondary">
                                    Service No.
                                </th>

                                <th className="px-6 py-4 text-left text-xs uppercase tracking-[2px] text-textSecondary">
                                    Name
                                </th>

                                <th className="px-6 py-4 text-center text-xs uppercase tracking-[2px] text-textSecondary">
                                    Mobile
                                </th>

                                <th className="px-6 py-4 text-center text-xs uppercase tracking-[2px] text-textSecondary">
                                    Role
                                </th>

                                <th className="px-6 py-4 text-center text-xs uppercase tracking-[2px] text-textSecondary">
                                    Status
                                </th>

                            </tr>

                        </thead>

                        <tbody>
                            {loading ? (
                                <tr className="border-t border-border hover:bg-slate-50 transition cursor-pointer">
                                    <td colSpan="5" className="p-8 text-center text-gray-500">
                                        Loading users...
                                    </td>
                                </tr>
                            ) : filteredUsers?.length === 0 ? (
                                <tr className="border-t border-border hover:bg-slate-50 transition cursor-pointer">
                                    <td colSpan="5" className="p-8 text-center text-gray-500">
                                        No users found
                                    </td>
                                </tr>
                            ) : (
                                filteredUsers.map((user) => (
                                    <tr
                                        key={
                                            user.ServiceNumber
                                        }
                                        onClick={() =>
                                            handleRowClick(
                                                user.ServiceNumber
                                            )
                                        }
                                        className="border-t border-border hover:bg-slate-50 transition cursor-pointer"
                                    >
                                        <td className="p-4 text-center lg:text-left">
                                            {user.ServiceNumber}
                                        </td>

                                        <td className="p-4">
                                            {user.Name}
                                        </td>

                                        <td className="p-4 text-center">
                                            {user.MobileNumber}
                                        </td>

                                        <td className="p-4 text-center">
                                            <span className={`
                                                px-3 py-2 rounded-full text-xs font-semibold
                                                ${user.Role === "A" ? "bg-electricBlue/10 text-electricBlue" : "bg-slate-100 text-slate-600"}  
                                            `}>
                                                {user.Role === "A" ? "Admin" : user.Role === "U" ? "User" : "Invalid" }
                                            </span>
                                        </td>

                                        <td className="p-4 text-center">
                                            <span className={`
                                                px-3 py-1 rounded-full text-xs font-semibold
                                                ${user.Status === "A" ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}  
                                            `}>
                                                {user.Status === "A" ? "Active" : user.Status === "I" ? "Inactive" : "Invalid" }
                                            </span>
                                        </td>

                                    </tr>

                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {open && (
                <UserModal
                    open={open}
                    user={user}
                    mode={modalMode}
                    onClose={() => {

                        dispatch(
                            clearUserDetails()
                        );

                        setOpen(false);
                    }}
                    onSubmit={handleCreate}
                    onUpdate={handleUpdate}
                />
            )}
        </>
    );
}

function StatCard({ label, value }) {
    return (
        <div className="bg-white border border-border rounded-3xl p-4 md:p-6">
            <p className="text-sm text-textSecondary">
                {label}
            </p>

            <p className="mt-2 text-3xl font-bold text-textPrimary">
                {value}
            </p>
        </div>
    )
}

export default UserManagement;